<template>
  <div class="gs-funnels-list">
    <div v-if="localFunnels.length" class="gs-funnels-list__inner">
      <div class="gs-funnels-list__summary">
        <div>
          <p class="gs-funnels-list__eyebrow">Product funnels</p>
          <h2>Manage every sales flow from one clean workspace.</h2>
        </div>
        <div class="gs-funnels-list__stats" role="group" aria-label="Filter product funnels by status">
          <button
            type="button"
            :class="{ 'is-active': statusFilter === 'all' }"
            :aria-pressed="statusFilter === 'all' ? 'true' : 'false'"
            @click="statusFilter = 'all'"
          >
            <strong>{{ localFunnels.length }}</strong> all
          </button>
          <button
            type="button"
            :class="{ 'is-active': statusFilter === 'live' }"
            :aria-pressed="statusFilter === 'live' ? 'true' : 'false'"
            @click="statusFilter = 'live'"
          >
            <strong>{{ liveFunnelsCount }}</strong> live
          </button>
          <button
            type="button"
            :class="{ 'is-active': statusFilter === 'not-live' }"
            :aria-pressed="statusFilter === 'not-live' ? 'true' : 'false'"
            @click="statusFilter = 'not-live'"
          >
            <strong>{{ draftFunnelsCount }}</strong> not live
          </button>
        </div>
      </div>
      <assets-list>
        <template slot="header-left">
          <label class="gs-funnels-list__search">
            <span class="sr-only">Search funnels</span>
            <input
              v-model.trim="searchQuery"
              type="search"
              placeholder="Search funnels"
            >
          </label>
          <div class="gs-funnels-list__sort">
            <gd-dropdown
              v-model="localSelectedOrder"
              size="auto"
              :options="sortOrderOptions"
              @change="changedSortOrder"
            />
          </div>
        </template>
        <template slot="gridContent">
          <div class="gs-funnels-list__grid">
            <funnel-grid-asset
              v-for="(funnel, index) in filteredFunnels" :key="index"
              :funnel="funnel"
              @onManageProducts="products"
              @onManagePromotools="funnelPromoTools"
              @onEditFunnel="$emit('onEditFunnel', $event)"
              @onPublishFunnel="$emit('onPublishFunnel', $event)"
              @onCloneFunnels="$emit('onCloneFunnels', $event)"
              @onDeleteFunnels="openConfirmModal"
            />
          </div>
        </template>
        <template slot="listContent">
          <div class="gs-funnels-list__rows">
            <div v-for="(funnel, index) in filteredFunnels" :key="index">
              <funnel-list-asset
                :funnel="funnel"
                @onManageProducts="products"
                @onManagePromotools="funnelPromoTools"
                @onEditFunnel="$emit('onEditFunnel', $event)"
              @onPublishFunnel="$emit('onPublishFunnel', $event)"
                @onCloneFunnels="$emit('onCloneFunnels', $event)"
                @onDeleteFunnels="openConfirmModal"
              />
            </div>
          </div>
        </template>
      </assets-list>
      <div v-if="!filteredFunnels.length" class="gs-funnels-list__no-results">
        <h2>No matching funnels</h2>
        <p>{{ noResultsMessage }}</p>
      </div>
    </div>
    <div v-else class="gs-funnels-list__empty">
      <h2>No product funnels yet</h2>
      <p>{{ $t('frontend_funnels_you_currently') }}</p>
    </div>
    <confirm-modal
      name="confirm-funnel-delete-modal"
      :message-content="$t('frontend_non_revertable')"
      :button-text="$t('frontend_delete_confirm')"
      button-size="auto"
      @confirm="deleteFunnel"
    />
  </div>
</template>

<script>
import AssetsList from '@/components/AssetsList';
import FunnelGridAsset from '@/components/FunnelGridAsset';
import FunnelListAsset from '@/components/FunnelListAsset';
import Funnel from '@/models/Funnel';

export default {
  components: {
    AssetsList,
    FunnelGridAsset,
    FunnelListAsset,
  },
  props: {
    funnels: {
      type: Array,
      required: true,
    },
    selectedOrder: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      localFunnels: this.funnels,
      localSelectedOrder: this.selectedOrder,
      searchQuery: '',
      statusFilter: 'all',
      funnelId: null,
    };
  },
  computed: {
    liveFunnelsCount() {
      return this.localFunnels.filter((funnel) => funnel.is_live == 1).length;
    },
    draftFunnelsCount() {
      return this.localFunnels.filter((funnel) => funnel.is_live != 1).length;
    },
    filteredFunnels() {
      const query = this.searchQuery.toLowerCase();
      const statusFiltered = this.localFunnels.filter((funnel) => {
        if (this.statusFilter === 'live') return funnel.is_live == 1;
        if (this.statusFilter === 'not-live') return funnel.is_live != 1;
        return true;
      });

      if (!query) return statusFiltered;

      return statusFiltered.filter((funnel) => {
        const searchable = [
          funnel.name,
          funnel.internal_name,
          funnel.slug,
          funnel.category,
          funnel.description,
        ].filter(Boolean).join(' ').toLowerCase();

        return searchable.includes(query);
      });
    },
    noResultsMessage() {
      if (this.searchQuery) {
        return 'Try searching by funnel name, slug, category, or description.';
      }

      if (this.statusFilter === 'live') return 'You do not have any live funnels yet.';
      if (this.statusFilter === 'not-live') return 'All of your funnels are live.';
      return 'Try another filter.';
    },
    sortOrderOptions() {
      return [
        { value: 'created_at', text: this.$t('frontend_groovesell_created_oldest_to_newest') },
        { value: '-created_at', text: this.$t('frontend_groovesell_created_newest_to_oldest') },
        { value: '-updated_at', text: this.$t('frontend_groovesell_updated_oldest_to_newest') },
        { value: 'updated_at', text: this.$t('frontend_groovesell_updated_newest_to_oldest') },
        { value: 'name', text: this.$t('frontend_name') + ' (A-Z)' },
        { value: '-name', text: this.$t('frontend_name') + ' (Z-A)' },
      ];
    },
  },
  methods: {
    products(funnel_id) {
      this.$emit('onManageProducts', funnel_id);
    },
    async changedSortOrder() {
      this.$loader.start('changedSortOrder');
      try {
        this.localFunnels = await Funnel.orderBy(this.localSelectedOrder).$get();
        this.$emit('updateFunnels', this.localFunnels);
        this.$emit('updateSelectedOrder', this.localSelectedOrder);
      } catch (error) {
        this.$gsToast.error(
          error?.response?.data?.message || 'GrooveSell could not reorder your funnels.'
        );
      } finally {
        this.$loader.end('changedSortOrder');
      }
    },
    funnelPromoTools(funnel_id) {
      this.$router.push({
        name: 'funnel_resources',
        params: { id: funnel_id },
      });
    },
    openConfirmModal(id) {
      this.$root.$emit('modal-open', 'confirm-funnel-delete-modal');
      this.funnelId = id;
    },
    async deleteFunnel() {
      this.$loader.start('deleteFunnel');
      try {
        const funnel = await Funnel.$find(this.funnelId);
        await funnel.delete();
        const deleted_funnel = this.localFunnels.map((item) => item.id).indexOf(this.funnelId);

        if (deleted_funnel !== -1) {
          this.localFunnels.splice(deleted_funnel, 1);
          this.$emit('updateFunnels', this.localFunnels);
        }

        this.$gsToast.success('Funnel deleted.');
      } catch (error) {
        this.$gsToast.error(
          error?.response?.data?.message || 'GrooveSell could not delete this funnel.'
        );
      } finally {
        this.$loader.end('deleteFunnel');
      }
    },
  },
};
</script>

<style lang="scss">
.gs-funnels-list {
  width: 100%;
}

.gs-funnels-list__inner {
  margin-top: 14px;
}

.gs-funnels-list__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  padding: 16px 18px;
  background: #ffffff;
  border: 1px solid #edf0f6;
  border-radius: 8px;
  box-shadow: 0 14px 32px rgba(31, 37, 72, .06);
}

.gs-funnels-list__eyebrow {
  margin: 0 0 4px;
  color: #ff5b7f;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.gs-funnels-list__summary h2 {
  max-width: 720px;
  margin: 0;
  color: #171b3f;
  font-size: 21px !important;
  font-weight: 650 !important;
  line-height: 1.3 !important;
}

.gs-funnels-list__stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.gs-funnels-list__stats button {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-height: 34px;
  padding: 6px 10px;
  color: #7c849c;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  background: #f7f8fc;
  border: 1px solid #edf0f6;
  border-radius: 6px;
  outline: none;
  transition: color .15s ease, background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
}

.gs-funnels-list__stats strong {
  color: #171b3f;
  font-size: 15px;
}

.gs-funnels-list__stats button:hover {
  color: #4f5873;
  border-color: #ffc0ce;
}

.gs-funnels-list__stats button:focus-visible {
  border-color: #ff5b7f;
  box-shadow: 0 0 0 3px rgba(255, 91, 127, .14);
}

.gs-funnels-list__stats button.is-active {
  color: #e54067;
  background: #fff8fa;
  border-color: #ff5b7f;
}

.gs-funnels-list__stats button.is-active strong {
  color: #171b3f;
}

.gs-funnels-list__search {
  position: relative;
  flex: 1 1 360px;
  max-width: 520px;
}

.gs-funnels-list__search input {
  width: 100%;
  min-height: 42px;
  padding: 0 14px;
  color: #171b3f;
  font-size: 14px;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #dfe4ee;
  border-radius: 8px;
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;
}

.gs-funnels-list__search input:focus {
  border-color: #ff8aa2;
  box-shadow: 0 0 0 3px rgba(255, 91, 127, .12);
}

.gs-funnels-list__search input::placeholder {
  color: #9aa1b7;
}

.gs-funnels-list__sort {
  width: min(260px, 100%);
}

.gs-funnels-list__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(285px, 1fr));
  gap: 16px 24px;
}

.gs-funnels-list__rows {
  display: grid;
  gap: 10px;
}

.gs-funnels-list__empty {
  margin-top: 34px;
  padding: 42px;
  text-align: center;
  background: #ffffff;
  border: 1px solid #edf0f6;
  border-radius: 8px;
}

.gs-funnels-list__empty h2 {
  margin: 0 0 8px;
  color: #171b3f;
  font-size: 24px;
  font-weight: 700;
}

.gs-funnels-list__empty p {
  margin: 0;
  color: #6c748e;
}

.gs-funnels-list__no-results {
  margin-top: 16px;
  padding: 34px;
  text-align: center;
  background: #ffffff;
  border: 1px solid #edf0f6;
  border-radius: 8px;
}

.gs-funnels-list__no-results h2 {
  margin: 0 0 8px;
  color: #171b3f;
  font-size: 22px;
  font-weight: 700;
}

.gs-funnels-list__no-results p {
  margin: 0;
  color: #6c748e;
}

@media (max-width: 780px) {
  .gs-funnels-list__summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .gs-funnels-list__stats {
    justify-content: flex-start;
  }
}
</style>
